import { Button, Card, Divider, Flex, Segmented, Space, Typography } from 'antd'
import { useNavigate } from '@tanstack/react-router'
import React from 'react'
import { ConnectionCard } from '../../facebook/components/connection-card'
import { IntegrationTokensList } from '../../integration-token/components/integration-tokens-list'
import { useAuth } from '../../auth'
import { INDEX_INTEGRATION_TOKEN, INDEX_WEBHOOK } from '../../user/permission'
import { useTranslation } from 'react-i18next'
import { WebhooksList } from '../../webhook/components/webhooks-list'
import { searchQuery } from '../../zod/pagination'
import { z } from 'zod'

import { AppConnections } from '../../app-connection/components/app-connections'
import { INDEX_APP_CONNECTION, INDEX_SITE } from '../../user/permission'
import { Link } from '@tanstack/react-router'
import { GlobalOutlined } from '@ant-design/icons'

export function IntegrationsPage({
    view,
    query,
}: {
    view?: 'tokens' | 'webhooks'
    query?: z.infer<typeof searchQuery>
}) {
    const { can } = useAuth()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const developerAccessOptions = [
        can(INDEX_INTEGRATION_TOKEN) && {
            label: t('Integration Tokens'),
            value: 'tokens',
        },
        can(INDEX_WEBHOOK) && {
            label: t('Webhooks'),
            value: 'webhooks',
        },
    ].filter(Boolean) as { label: string; value: 'tokens' | 'webhooks' }[]
    const showDeveloperAccess = can(INDEX_INTEGRATION_TOKEN) || can(INDEX_WEBHOOK)
    const developerAccessView =
        developerAccessOptions.find((option) => option.value === view)?.value ??
        developerAccessOptions[0]?.value ??
        'tokens'

    return (
        <Flex
            gap="large"
            vertical
        >
            <Flex
                gap="large"
                className="flex-col md:flex-row"
                align="stretch"
            >
                <Flex
                    vertical
                    className="w-full md:flex-1"
                >
                    <ConnectionCard />
                </Flex>
                <Card className="w-full md:flex-1">
                    <Flex gap="middle">
                        <img
                            alt="zapier"
                            className="max-h-18"
                            src="https://cdn.zapier.com/zapier/images/logomark250.png"
                        />
                        <a
                            rel="noreferrer"
                            target="_blank"
                            href="https://zapier.com/developer/public-invite/229398/17e317ca158b490c08e2aed0da9ae5e0/"
                        >
                            <Button>{t('Invite')}</Button>
                        </a>
                    </Flex>
                </Card>
            </Flex>

            {can(INDEX_APP_CONNECTION) && <AppConnections />}

            {can(INDEX_SITE) && (
                <Card>
                    <Flex
                        justify="space-between"
                        align="center"
                        gap="middle"
                        wrap
                    >
                        <Flex
                            gap="middle"
                            align="center"
                        >
                            <GlobalOutlined className="text-2xl" />
                            <Flex vertical>
                                <Typography.Text strong>{t('Websites')}</Typography.Text>
                                <Typography.Text type="secondary">
                                    {t("Use Evaa on your website to answer your visitors' questions.")}
                                </Typography.Text>
                            </Flex>
                        </Flex>
                        <Link to="/websites">
                            <Button>{t('Manage websites')} →</Button>
                        </Link>
                    </Flex>
                </Card>
            )}

            {showDeveloperAccess && (
                <>
                    <Divider className="my-0" />

                    <Flex
                        vertical
                        gap="small"
                    >
                        <Flex
                            justify="space-between"
                            align="center"
                            gap="middle"
                            className="flex-col md:flex-row md:items-start"
                        >
                            <Flex
                                vertical
                                gap="small"
                            >
                                <Typography.Title
                                    level={4}
                                    className="mb-0"
                                >
                                    {t('Developer access')}
                                </Typography.Title>
                                <Typography.Text type="secondary">
                                    {t('Create tokens and webhooks for custom integrations and backend workflows.')}
                                </Typography.Text>
                            </Flex>

                            <Space>
                                <a
                                    target="_blank"
                                    href="/docs/integration.html"
                                >
                                    <Button>{t('Documentation')}</Button>
                                </a>
                            </Space>
                        </Flex>
                    </Flex>

                    {developerAccessOptions.length > 1 && (
                        <Segmented
                            block
                            value={developerAccessView}
                            options={developerAccessOptions}
                            onChange={(value) =>
                                navigate({
                                    to: '/integrations',
                                    search: { view: value as 'tokens' | 'webhooks' },
                                })
                            }
                        />
                    )}

                    {developerAccessView === 'tokens' && can(INDEX_INTEGRATION_TOKEN) && <IntegrationTokensList />}
                    {developerAccessView === 'webhooks' && can(INDEX_WEBHOOK) && <WebhooksList query={query} />}
                </>
            )}
        </Flex>
    )
}
